import {
  ChangeEvent,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  EMAIL_SUBJECT_MAX_SAFE,
  EmailTemplateTypes,
  IEmailTemplateChild,
  SignaturePositions,
  generateHtml,
} from 'shared';
import { useParams } from 'react-router';
import { JSONContent } from '@tiptap/core';
import {
  ArrowUpTrayIcon,
  PaperClipIcon,
  PencilSquareIcon,
  SparklesIcon,
} from '@heroicons/react/24/outline';
import { format } from 'date-fns';
import { Tooltip } from '@mui/material';
import { When } from 'react-if';
import mustache from 'mustache';
import { toast } from 'react-toastify';

import { Card, ForwardRefCard } from '../../../../common/Atoms/Card';
import { Badge } from '../../../../common/Atoms/Badge';
import { CardContent } from '../../../../common/Atoms/CardContent';
import {
  useRewriteEmailTemplateToneMutation,
  useSendTestEmailMutation,
  useUpdateEmailTemplateMutation,
} from '../../../services/api/emailTemplates/emailTemplates';
import { FormWithButton } from '../../Molecules/Form/FormWithButton';
import { FormRow } from '../../Molecules/Form/FormRow';
import { InputTextAddon } from '../../../../common/Atoms/InputTextAddon';
import { useMergeTags } from '../../../hooks/useMergeTagsAndLinks';
import { MultiButtonCardHeader } from '../../../../common/Atoms/MultiButtonCardHeader';
import { Grid } from '../../../../common/Atoms/Grid';
import { ForwardRefGridItem, GridItem } from '../../../../common/Atoms/GridItem';
import { CustomIframe } from '../../IFrame';
import { useEmailTemplates } from '../../../hooks/useEmailTemplates';
import { TextEditor } from '../../Molecules/TextEditor/TextEditor';
import {
  IDynamicSection,
  SectionTag,
  sectionTagText,
} from '../../Molecules/TextEditor/dynamicSections';
import { validateForm } from '../../../services/forms/validate';
import { capitalizeFirstLetter, fieldIsValid } from '../../../lib/helper';
import { useGetDefaultSenderProfile, useSettings } from '../../../hooks/useSettings';
import { useUser } from '../../../hooks/useUser';
import { useFormDataDirty } from '../../../hooks/useFormDataDirty';
import { Notification } from '../../../../common/Atoms/Notification';
import { HelpIcon } from '../../Molecules/HelpIcon';
import { useGetSelectedOrganisation } from '../../../hooks/useGetSelectedOrganisation';
import { useFiles } from '../../../hooks/useFiles';
import { useUploadFileMutation } from '../../../services/api/filesApi/files';
import { useCustomDomain } from '../../../hooks/useCustomDomain';
import { MultiSelect } from '../../../../common/Atoms/MultiSelect';
import { Select } from '../../../../common/Atoms/Select';
import { Button } from '../../../../common/Atoms/Button';
import { Modal } from '../../../../common/Atoms/Modal';
import { Paragraph } from '../../../../common/Atoms/Typography/Paragraph';
import { Heading } from '../../../../common/Atoms/Typography/Heading';
import { classNames } from '../../../../common/lib/classNames';

import { types, VARIATIONS_EXPLAINER } from './constants';
import { EmailTemplateVariations } from './EmailTemplateVariations';

const MAX_TONE_WORDS = 3;

const sectionTags: SectionTag[] = [`PenaltiesSection`, `PaymentPlanSection`];

type VariationContent = Pick<IEmailTemplateChild, `subject` | `main_html_content` | `main_json_content`>;

export function EmailTemplate() {
  const { organisationId, templateId } = useParams();
  const { data: emailTemplates } = useEmailTemplates();
  const { data: user } = useUser();
  const defaultProfile = useGetDefaultSenderProfile();
  const { data: filesResponse, isLoading: filesLoading } = useFiles();
  const parentRef = useRef(null);
  const scrollingDivRef = useRef(null);
  const [ updateTemplate, { isLoading: isLoadingUpdate }] = useUpdateEmailTemplateMutation();
  const [ sendTestEmail, { isLoading: sendTestEmailLoading }] = useSendTestEmailMutation();
  const [ rewriteTone, { isLoading: rewriteToneLoading }] = useRewriteEmailTemplateToneMutation();
  const [ uploadFile, { isLoading: uploadingFile }] = useUploadFileMutation();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [rewriteToneModalOpen, setRewriteToneModalOpen] = useState(false);
  const [filesModalOpen, setFilesModalOpen] = useState(false);
  const [tone, setTone] = useState(``);
  const [invalids, setInvalids] = useState([]);
  const [senderProfileId, setSenderProfileId] = useState<number | null>(null);
  const [previewDevice, setPreviewDevice] = useState<`desktop` | `mobile`>(`desktop`);
  const [activeVariationId, setActiveVariationId] = useState<string | null>(null);
  // Unsaved edits to variations, keyed by variation id
  const [variationDrafts, setVariationDrafts] = useState<Record<string, VariationContent>>({});
  const [formData, setFormData] = useState({
    name: ``,
    subject: ``,
    primary_color: ``,
    secondary_color: ``,
    include_upcoming_latefee: true,
    main_json_content: null,
    main_html_content: null,
    secondary_json_content: null as JSONContent | null,
    secondary_html_content: ``,
    type: EmailTemplateTypes.REMINDER,
    attached_files: [],
    signature_position: null,
    migrated_to_new_logo_sig: undefined as boolean | undefined,
    include_payment_plan_section: false,
    payment_plan_html_content: ``,
    payment_plan_json_content: null,
  });
  const { data: settings } = useSettings();
  const { data: customDomain } = useCustomDomain();
  const currentOrg = useGetSelectedOrganisation();

  // Drives both the preview and test emails, falls back to the default profile
  const senderProfile = settings?.senderProfiles.find(p => p.id === senderProfileId) ?? defaultProfile;

  const logoUrl = useMemo(() => {
    if (senderProfile?.hideLogo) return null;

    // Kept narrow enough for the mobile preview frame: the email markup caps the logo at 560px
    // wide rather than at the container width, so a full-width placeholder overflows on mobile.
    const placeholder = `https://placehold.co/280x50?text=Your+Logo&font=roboto`;

    return senderProfile?.resizedLogoUrl || settings?.resizedLogoUrl || placeholder;
  }, [ settings, senderProfile ]);

  const template = useMemo(() => {
    if (!emailTemplates) return null;

    return emailTemplates.find(t => t.id === templateId);
  }, [emailTemplates, templateId]);

  const fileOptions = useMemo(() => {
    if (!filesResponse) return [];

    return filesResponse.files.map(f => ({
      value: f.id,
      label: f.originalFileName,
    }));
  }, [ filesResponse ]);

  const senderProfileOptions = useMemo(() => {
    return (settings?.senderProfiles || []).map(profile => ({
      label: profile.name,
      value: profile.id,
      badge: profile.default ? `Default` : undefined,
      badgeColor: profile.default ? `blue` : undefined,
    }));
  }, [ settings ]);

  const mergeTags = useMergeTags(template?.type);

  // Sections are inserted as blocks from the editor toolbar, not as merge tags
  const mergeTagsForPrimaryEditor = useMemo(() => {
    return mergeTags.asArray
      .filter(t => !t.archived && !sectionTags.includes(t.value as SectionTag))
      // An unmoved template still draws the fixed logo and signature, so a block would send it twice
      .filter(t => !t.block || formData.migrated_to_new_logo_sig)
      .map(t => ({
        text: t.name,
        value: t.value,
        new: t.new,
        link: t.link,
        block: t.block,
        section: t.value === `PenaltyForecastAmount` ? `PenaltiesSection` as const : undefined,
      }));
  }, [ mergeTags, formData.migrated_to_new_logo_sig ]);

  const sections = useMemo<IDynamicSection[] | undefined>(() => {
    if (template?.type !== EmailTemplateTypes.REMINDER) return undefined;

    return [
      {
        tag: `PenaltiesSection`,
        label: `Upcoming penalties`,
        description: `Included when the invoice has late fee policies that apply`,
        json: formData.secondary_json_content,
        html: formData.secondary_html_content || ``,
        enabled: formData.include_upcoming_latefee,
      },
      {
        tag: `PaymentPlanSection`,
        label: `Payment plan`,
        description: `Included when the invoice is eligible for a payment plan`,
        json: formData.payment_plan_json_content as JSONContent || null,
        html: formData.payment_plan_html_content || ``,
        enabled: !!formData.include_payment_plan_section,
      },
    ];
  }, [
    template?.type,
    formData.secondary_json_content,
    formData.secondary_html_content,
    formData.include_upcoming_latefee,
    formData.payment_plan_json_content,
    formData.payment_plan_html_content,
    formData.include_payment_plan_section,
  ]);

  useEffect(() => {
    if (!template) return;

    const form = {
      name: template.name,
      subject: template.subject,
      primary_color: template.primary_color,
      secondary_color: template.secondary_color,
      include_upcoming_latefee: !!template.include_upcoming_latefee,
      main_json_content: template.main_json_content,
      secondary_json_content: template.secondary_json_content,
      main_html_content: template.main_html_content,
      secondary_html_content: template.secondary_html_content,
      type: template.type,
      attached_files: template.attached_files,
      signature_position: template.signature_position,
      // Sent back on save so the server can tell the body was loaded before the logo and signature move
      migrated_to_new_logo_sig: template.migrated_to_new_logo_sig,
      payment_plan_html_content: template.payment_plan_html_content,
      payment_plan_json_content: template.payment_plan_json_content,
      include_payment_plan_section: template.include_payment_plan_section,
    };

    setFormData(form);
  }, [ template ]);

  useEffect(() => {
    setActiveVariationId(null);
    setVariationDrafts({});
  }, [template?.id]);

  const activeVariation = template?.children.find(c => c.id === activeVariationId) ?? null;
  // The subject and body editors show the active variation, everything else always belongs to the original
  const activeContent: VariationContent = activeVariation ? variationDrafts[activeVariation.id] ?? activeVariation : formData;

  const unsavedVariations = (template?.children ?? []).filter(c => {
    const draft = variationDrafts[c.id];

    return draft && (draft.subject !== c.subject || draft.main_html_content !== c.main_html_content);
  });

  const isOriginalDirty = useFormDataDirty(template, formData, [
    `main_json_content`,
    `main_html_content`,
    `secondary_html_content`,
    `secondary_json_content`,
    `payment_plan_html_content`,
    `payment_plan_json_content`,
    `updated_at`,
    `created_at`,
    `id`,
    `organisation_id`,
    `user_id`,
    `version`,
    `user`,
    `primary_color`,
    `secondary_color`,
    `legacy_logo_url`,
    `attached_files`,
    `policies`,
    `children`,
    `parent_template_id`,
    `deleted`,
    `main_html_content_pre_logo_sig`,
    `main_json_content_pre_logo_sig`,
    `migrated_to_new_logo_sig`,
  ]);
  const isDirty = isOriginalDirty || unsavedVariations.length > 0;

  // Generates the preview
  const html = useMemo(() => {
    if (!mergeTags || !mergeTags.asTestData) return ``;
    if (Object.keys(mergeTags.asTestData).length === 0) return ``;
    if (!senderProfile) return ``;

    const previewForm = {
      ...formData,
      subject: activeContent.subject,
      main_html_content: activeContent.main_html_content,
    };

    if (!previewForm.main_html_content) return ``;

    const htmlTemplate = generateHtml(previewForm, logoUrl, currentOrg.removePaidniceBranding, senderProfile.signatureHtml);

    // Now merge it with test data
    try {
      return mustache.render(htmlTemplate, mergeTags.asTestData);
    }
    catch {
      // Half typed merge tags fail to parse, show the raw template until they are complete
      return htmlTemplate;
    }

  }, [ formData, mergeTags, senderProfile, activeContent, logoUrl, currentOrg.removePaidniceBranding ]);

  const previewSubject = useMemo(() => {
    try {
      return mustache.render(activeContent.subject ?? ``, mergeTags.asTestData);
    }
    catch {
      // Half typed merge tags fail to parse, show the raw subject until they are complete
      return activeContent.subject;
    }
  }, [ activeContent.subject, mergeTags ]);

  const domainActive = !!customDomain?.verified && !!customDomain?.domain;
  const fromAddress = domainActive ? `${senderProfile?.fromAddress}@${customDomain.domain}` : `no-reply@mail.paidnice.com`;
  const senderInitials = (senderProfile?.fromName ?? ``).split(/\s+/).filter(Boolean).slice(0, 2).map(w => w[0].toUpperCase()).join(``);
  const testRecipient = user?.adminOverride ? `your admin email` : user?.email;
  // The preview falls back to a placeholder image when the org has no logo; it is never sent
  const usingPlaceholderLogo = !settings?.logoUrl && !senderProfile?.logoUrl && !senderProfile?.hideLogo;

  const positionOptions = Object.values(SignaturePositions).map(p => ({
    label: capitalizeFirstLetter(p),
    value: p,
  }));

  useEffect(() => {
    const handleScroll = () => {
      const parent = parentRef.current;
      const scrollingDiv = scrollingDivRef.current;

      if (!parent || !scrollingDiv) return;

      const parentRect = parent.getBoundingClientRect();
      const scrollingDivRect = scrollingDiv.getBoundingClientRect();
      const viewHeight = window.innerHeight;
      // Amount of pixels to make the div sit higher
      const higherByPixels = 300;

      // Calculate the mid-position between the top of the parent and the bottom of the view.
      const midPosition = Math.max(0, Math.min(parentRect.bottom - scrollingDivRect.height, viewHeight / 2 - scrollingDivRect.height / 2)) - higherByPixels;

      // Calculate how much the div should be offset from the top of the parent to be at the mid-position.
      let yOffset = midPosition - parentRect.top + scrollingDivRect.height / 2;

      // Special case: when the parent is at the top of the viewport
      if (parentRect.top > 1) {
        yOffset = 0;
      }

      if (parentRect.bottom < viewHeight) {
        yOffset = parentRect.height;
      }

      // Clamp yOffset so the div does not go outside of the parent.
      yOffset = Math.max(0, Math.min(yOffset, parent.clientHeight - scrollingDiv.clientHeight));

      scrollingDiv.style.top = `${yOffset}px`;
    };

    window.addEventListener(`scroll`, handleScroll);

    return () => window.removeEventListener(`scroll`, handleScroll);
  }, []);

  function onChange(e) {
    setFormData({
      ...formData,
      [e.name]: e.value,
    });
  }

  function handleActiveContentChange(update: Partial<VariationContent>) {
    if (!activeVariation) {
      setFormData(prev => ({ ...prev, ...update }));

      return;
    }

    setVariationDrafts(prev => {
      const base = prev[activeVariation.id] ?? activeVariation;
      // AI variations have no json, so the editor rebuilds it from the html on load. That rebuild is not an edit,
      // so keep the stored html and the variation stays clean
      const isJsonRebuild = !base.main_json_content && !!update.main_json_content;

      return {
        ...prev,
        [activeVariation.id]: {
          ...base,
          ...update,
          ...(isJsonRebuild && { main_html_content: base.main_html_content }),
        },
      };
    });
  }

  // A section is included while any body (the original or a variation) has it. Its content belongs to the original,
  // and is kept when the section is removed so adding it back restores it
  function handleSectionsChange(html: string, content: Partial<Record<SectionTag, { json: JSONContent; html: string }>> = {}) {
    if (!sections) return;

    const otherBodies = [
      activeVariation && formData.main_html_content,
      ...template.children.filter(c => c.id !== activeVariation?.id).map(c => (variationDrafts[c.id] ?? c).main_html_content),
    ];

    const included = (tag: SectionTag, current: boolean) => {
      if (html.includes(sectionTagText(tag))) return true;

      return otherBodies.some(b => b?.includes(sectionTagText(tag))) ? current : false;
    };

    const penalties = !activeVariation && content.PenaltiesSection;
    const paymentPlan = !activeVariation && content.PaymentPlanSection;

    setFormData(prev => ({
      ...prev,
      include_upcoming_latefee: included(`PenaltiesSection`, prev.include_upcoming_latefee),
      include_payment_plan_section: included(`PaymentPlanSection`, !!prev.include_payment_plan_section),
      ...(penalties && { secondary_json_content: penalties.json, secondary_html_content: penalties.html }),
      ...(paymentPlan && { payment_plan_json_content: paymentPlan.json, payment_plan_html_content: paymentPlan.html }),
    }));
  }

  const onSave = async e => {
    if (e) {
      e.preventDefault();
    }

    const updateParams = {
      ...formData,
      org: organisationId,
      id: templateId,
    };

    const validateResult = await validateForm(updateParams, `EMAIL_TEMPLATE`);

    if (!validateResult.valid) {
      setInvalids(validateResult.invalids);

      return;
    }

    const invalidVariation = unsavedVariations.find(v => !variationDrafts[v.id].subject?.trim() || !variationDrafts[v.id].main_html_content?.trim());

    if (invalidVariation) {
      setActiveVariationId(invalidVariation.id);
      toast.error(`Variations need a subject and body`);

      return;
    }

    setInvalids([]);

    const result = await updateTemplate(validateResult.form);

    if (result.error) return;

    // One at a time, as saving the original also bumps each variation's version
    for (const variation of unsavedVariations) {
      const draft = variationDrafts[variation.id];
      const variationResult = await updateTemplate({
        org: organisationId,
        id: variation.id,
        subject: draft.subject,
        main_html_content: draft.main_html_content,
        main_json_content: draft.main_json_content,
      });

      if (variationResult.error) return;
    }
  };

  const toneWordCount = tone.trim().split(/\s+/).filter(Boolean).length;

  async function onRewriteTone() {
    if (toneWordCount < 1 || toneWordCount > MAX_TONE_WORDS || rewriteToneLoading) return;

    try {
      const result = await rewriteTone({
        org: organisationId,
        id: templateId,
        tone: tone.trim(),
        subject: formData.subject,
        mainHtmlContent: formData.main_html_content,
        secondaryHtmlContent: formData.secondary_html_content || ``,
        paymentPlanHtmlContent: formData.payment_plan_html_content || ``,
      }).unwrap();

      // JSON is cleared so each editor rebuilds it from the new HTML. Empty sections are left alone.
      // Nothing is saved until the user saves the form
      setFormData(prev => ({
        ...prev,
        subject: result.subject,
        main_html_content: result.mainHtmlContent,
        main_json_content: null,
        ...(result.secondaryHtmlContent && {
          secondary_html_content: result.secondaryHtmlContent,
          secondary_json_content: null,
        }),
        ...(result.paymentPlanHtmlContent && {
          payment_plan_html_content: result.paymentPlanHtmlContent,
          payment_plan_json_content: null,
        }),
      }));

      setRewriteToneModalOpen(false);
      setTone(``);

      toast.success(`Rewritten. Review the changes and save the template to keep them`);
    }
    catch (err) {
      const status = (err as { status?: number })?.status;
      const message = (err as { data?: { message?: string } })?.data?.message;

      toast.error(status === 429 ? `Too many rewrites, please try again later` : message || `The AI rewrite failed, please try again`);
    }
  }

  function sendTestEmailFn() {
    sendTestEmail({
      org: organisationId,
      templateId,
      senderProfileId: senderProfile?.id,
    });
  }

  function onChangeFiles(e) {
    const files = filesResponse.files.filter(f => e.includes(f.id));

    setFormData({
      ...formData,
      attached_files: files,
    });
  }

  async function onUploadFile(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = ``;
    if (!file) return;

    const body = new FormData();
    body.append(`file`, file);

    try {
      const uploaded = await uploadFile({ org: organisationId, formData: body }).unwrap();

      // Attach the new file straight away, it won't be in the files list until the refetch lands
      setFormData(prev => ({
        ...prev,
        attached_files: [...(prev.attached_files || []), uploaded],
      }));
    }
    catch (err) {
      // The API sends upload errors (duplicate name, storage limit) as a plain string
      const message = (err as { data?: unknown })?.data;

      toast.error(typeof message === `string` && message ? message : `Could not upload the file, please try again`);
    }
  }

  const attachedFilesDisplay = useMemo(() => {
    if (fileOptions.length === 0 && !filesLoading) {
      return `No Files Uploaded`;
    }

    if (!formData.attached_files || formData.attached_files.length === 0) {
      return `No Files Attached`;
    }

    if (formData.attached_files.length === 1) {
      return formData.attached_files[0].originalFileName;
    }

    return `${formData.attached_files.length} files attached`;

  }, [ formData.attached_files, fileOptions, filesLoading ]);

  /** Short label beside the paperclip: one file name, or a count once there are several */
  const attachedFilesLabel = useMemo(() => {
    const attached = formData.attached_files || [];

    if (!attached.length) {
      return null;
    }

    if (attached.length === 1) {
      return attached[0].originalFileName;
    }

    return `${attached.length} files`;
  }, [ formData.attached_files ]);

  const attachedFilesValue = useMemo(() => {
    if (!formData.attached_files) return [];

    return formData.attached_files.map(f => f.id);
  }
  , [ formData.attached_files ]);

  const version = template?.version < 1 ? `Draft` : template?.version;
  const formatted = format(new Date(template?.updated_at || null), `dd LLL yy h:mm a`);

  if (!template) return null;

  return (
    <Grid cols={ 12 }
      className={ `` }>
      <GridItem span={ 12 }>
        <When condition={ isDirty }>
          <Notification
            type={ `warning` }
          >
            { `You have unsaved changes` }
          </Notification>
        </When>
      </GridItem>
      <GridItem span={ 5 }>
        <Card className={ `relative` }>
          <Badge
            className={ `absolute right-3 top-3` }
            message={ types[template.type] }
          />
          <MultiButtonCardHeader
            title={
              <label className={ `group -ml-1.5 flex items-center gap-1 cursor-text` }>
                <input
                  type={ `text` }
                  aria-label={ `Template name` }
                  form={ `email-template-form` }
                  name={ `name` }
                  value={ formData.name ?? `` }
                  onChange={ e => onChange({ name: `name`, value: e.target.value }) }
                  required
                  size={ Math.max(formData.name?.length ?? 0, 10) }
                  className={ classNames(
                    `rounded-md border bg-transparent px-1.5 py-0.5 text-base leading-6 font-semibold text-gray-900 hover:border-gray-300 focus:border-paidniceGreen-500 focus:ring-paidniceGreen-500`,
                    fieldIsValid(`name`, invalids) ? `border-red-600` : `border-transparent`,
                  ) }
                />
                <PencilSquareIcon className={ `h-4 w-4 shrink-0 text-gray-400 opacity-0 transition-opacity group-hover:opacity-100 group-focus-within:opacity-100` } />
              </label>
            }
            noDivider
          />
          <CardContent>
            <div className={ `flex items-center gap-2` }>
              <div className={ `min-w-0 flex-1` }>
                <EmailTemplateVariations
                  organisationId={ organisationId }
                  templateId={ templateId }
                  variations={ template.children }
                  unsavedVariationIds={ unsavedVariations.map(v => v.id) }
                  isDirty={ isDirty }
                  activeVariationId={ activeVariation?.id ?? null }
                  onActiveVariationChange={ setActiveVariationId }
                />
              </div>
              <When condition={ !!template.children?.length }>
                <HelpIcon
                  className={ `shrink-0` }
                  tooltip={ VARIATIONS_EXPLAINER }
                />
              </When>
            </div>
            <FormWithButton
              id={ `email-template-form` }
              className={ `pt-6` }
              onSubmit={ onSave }
              submitText={ `Save Template` }
              loading={ isLoadingUpdate }
              invalids={ invalids }
            >
              <FormRow>
                <TextEditor
                  // Remount per tab so the editor's update handler belongs to the content it shows
                  key={ activeVariation?.id ?? `original` }
                  mergeTags={ mergeTagsForPrimaryEditor }
                  logoUrl={ logoUrl }
                  subject={ {
                    value: activeContent.subject ?? ``,
                    onChange: subject => handleActiveContentChange({ subject }),
                    maxLength: EMAIL_SUBJECT_MAX_SAFE,
                    invalid: !activeVariation && !!fieldIsValid(`subject`, invalids),
                  } }
                  toolbarAction={ {
                    label: `Rewrite tone`,
                    icon: <SparklesIcon className={ `h-3.5 w-3.5` } />,
                    disabled: !formData.main_html_content || !!activeVariation,
                    onClick: () => setRewriteToneModalOpen(true),
                  } }
                  toolbarExtra={ (
                    <Tooltip title={ attachedFilesDisplay }>
                      <button
                        type={ `button` }
                        onClick={ () => setFilesModalOpen(true) }
                        disabled={ !!activeVariation }
                        aria-label={ `Attached files` }
                        className={ classNames(
                          `flex h-7 min-w-7 max-w-[220px] items-center justify-center gap-1 rounded-md px-1.5 text-[13px] hover:bg-gray-100`,
                          attachedFilesLabel ? `text-blue-600` : `text-gray-600 hover:text-gray-900`,
                          activeVariation && `!cursor-not-allowed opacity-50`,
                        ) }
                      >
                        <PaperClipIcon className={ `h-4 w-4 shrink-0` } />
                        <When condition={ !!attachedFilesLabel }>
                          <span className={ `truncate` }>{ attachedFilesLabel }</span>
                        </When>
                      </button>
                    </Tooltip>
                  ) }
                  onChange={ ({ json, html, sections: sectionContent }) => {
                    handleActiveContentChange({ main_json_content: json, main_html_content: html });
                    handleSectionsChange(html, sectionContent);
                  } }
                  value={ activeContent.main_json_content as JSONContent }
                  htmlValue={ activeContent.main_html_content }
                  sections={ sections }
                  lockSections={ !!activeVariation }
                />
              </FormRow>

              <div
                inert={ !!activeVariation }
                className={ classNames(activeVariation && `opacity-50`) }
              >
                { /* Moved templates place the signature with the {{{Signature}}} block instead */ }
                <When condition={ !formData.migrated_to_new_logo_sig }>
                  <Select
                    label={ `Signature Position` }
                    selected={ formData.signature_position }
                    nullable
                    options={ positionOptions }
                    onChange={ onChange }
                    name={ `signature_position` }
                    className={ `mb-4` }
                  />
                </When>

                <When condition={ template?.policies?.length > 1 }>
                  <Notification
                    type={ `info` }
                  >
                    { `This template is used in ${template.policies.length} policies` }
                  </Notification>
                </When>
              </div>

            </FormWithButton>

            <p className={ `pt-8 text-left text-xs text-gray-400 opacity-50` }>
              { `Last updated on ${formatted}. Version: ${version}` }
            </p>
          </CardContent>
        </Card>
      </GridItem>
      <ForwardRefGridItem span={ 7 }
        className={ `relative h-full` }
        ref={ parentRef }>
        <ForwardRefCard
          className={ `p-0 sm:p-0 overflow-hidden absolute inset-x-0 my-1/4 transition-all duration-500 ease-in-out` }
          style={ { transform: `translateY(-50%)` } }
          ref={ scrollingDivRef }
        >
          <MultiButtonCardHeader
            title={
              <div className={ `flex items-center gap-x-3` }>
                <Heading>{ `Live Preview` }</Heading>
                <When condition={ senderProfileOptions.length > 1 }>
                  <Select
                    options={ senderProfileOptions }
                    selected={ senderProfile?.id }
                    onChange={ ({ value }) => setSenderProfileId(Number(value)) }
                    name={ `sender_profile_id` }
                    size={ `sm` }
                    style={ `light` }
                    className={ `w-64 shrink-0` }
                    valueClassname={ `font-normal text-gray-500 whitespace-nowrap` }
                  />
                </When>
              </div>
            }
            cta={
              <>
                <div className={ `ml-4 mt-2 flex gap-0.5 rounded-md bg-gray-100 p-0.5` }>
                  { ([`desktop`, `mobile`] as const).map(d => (
                    <button
                      key={ d }
                      type={ `button` }
                      onClick={ () => setPreviewDevice(d) }
                      className={ classNames(
                        `cursor-pointer rounded px-3 py-1 text-xs`,
                        previewDevice === d ? `bg-white font-semibold text-gray-900 shadow-sm` : `text-gray-500 hover:text-gray-700`,
                      ) }
                    >
                      { capitalizeFirstLetter(d) }
                    </button>
                  )) }
                </div>
                <Button
                  className={ `ml-4 mt-2` }
                  size={ `sm` }
                  onClick={ sendTestEmailFn }
                  loading={ sendTestEmailLoading }
                  disabled={ isDirty }
                  tooltip={ `Send a test email to ${testRecipient}, using real data if possible` }
                >
                  { isDirty ? `Unsaved Changes` : `Send a test email` }
                </Button>
              </>
            }
          />

          <div className={ `flex min-h-[760px] justify-center bg-gray-200 p-7` }>
            <When condition={ previewDevice === `desktop` }>
              <div className={ `w-full max-w-[640px] self-start overflow-hidden rounded-lg bg-white shadow-md` }>
                <div className={ `flex items-center gap-1.5 border-b border-gray-200 bg-gray-50 px-3.5 py-2.5` }>
                  <span className={ `h-2.5 w-2.5 rounded-full bg-red-400` } />
                  <span className={ `h-2.5 w-2.5 rounded-full bg-amber-400` } />
                  <span className={ `h-2.5 w-2.5 rounded-full bg-emerald-400` } />
                  <span className={ `flex-1 truncate text-center text-xs text-gray-500` }>{ previewSubject }</span>
                </div>
                <div className={ `flex flex-col gap-0.5 border-b border-gray-200 px-5 py-4 text-sm` }>
                  <div className={ `flex min-w-0 justify-between gap-3` }>
                    <span className={ `truncate` }>
                      <strong>{ senderProfile?.fromName }</strong>
                      <span className={ `text-gray-500` }>{ ` <${fromAddress}>` }</span>
                    </span>
                    <span className={ `shrink-0 text-xs text-gray-500` }>{ format(new Date(), `h:mm a`) }</span>
                  </div>
                  <span className={ `truncate text-gray-500` }>{ `To: ${testRecipient}` }</span>
                </div>
                <When condition={ usingPlaceholderLogo }>
                  <p className={ `border-b border-gray-100 bg-gray-50 px-5 py-1.5 text-center text-[11px] text-gray-400` }>
                    { `Placeholder logo. It is not included on real emails.` }
                  </p>
                </When>
                <CustomIframe
                  className={ `h-[640px] w-full` }
                  srcDoc={ html }
                />
              </div>
            </When>
            <When condition={ previewDevice === `mobile` }>
              <div className={ `h-[700px] w-[340px] self-start rounded-[44px] bg-gray-900 p-3 shadow-xl` }>
                <div className={ `flex h-full w-full flex-col overflow-hidden rounded-[34px] bg-white` }>
                  <div className={ `flex items-center justify-between px-5 pb-1.5 pt-3 text-xs font-semibold` }>
                    <span>{ format(new Date(), `h:mm`) }</span>
                    <span className={ `h-5 w-[70px] rounded-xl bg-gray-900` } />
                    <span>{ `100%` }</span>
                  </div>
                  <div className={ `flex justify-between px-4 py-2 text-sm text-blue-600` }>
                    <span>{ `‹ Inbox` }</span>
                    <span className={ `text-gray-400` }>{ `•••` }</span>
                  </div>
                  <div className={ `flex flex-col gap-2 border-b border-gray-200 px-4 pb-3 pt-1.5` }>
                    <span className={ `text-lg font-bold leading-tight` }>{ previewSubject }</span>
                    <div className={ `flex min-w-0 items-center gap-2` }>
                      <span className={ `flex h-[30px] w-[30px] shrink-0 items-center justify-center rounded-full bg-orange-500 text-[11px] font-semibold text-white` }>
                        { senderInitials }
                      </span>
                      <div className={ `flex min-w-0 flex-col text-xs` }>
                        <strong className={ `truncate` }>{ senderProfile?.fromName }</strong>
                        <span className={ `truncate text-gray-500` }>{ `To: ${testRecipient}` }</span>
                      </div>
                    </div>
                  </div>
                  <When condition={ usingPlaceholderLogo }>
                    <p className={ `border-b border-gray-100 bg-gray-50 px-5 py-1.5 text-center text-[11px] text-gray-400` }>
                      { `Placeholder logo. It is not included on real emails.` }
                    </p>
                  </When>
                  <CustomIframe
                    className={ `w-full flex-1` }
                    srcDoc={ html }
                  />
                </div>
              </div>
            </When>
          </div>
        </ForwardRefCard>
      </ForwardRefGridItem>

      <Modal
        title={ `Attached files` }
        open={ filesModalOpen }
        setOpen={ setFilesModalOpen }
      >
        <MultiSelect
          label={ `Attached Files` }
          options={ fileOptions }
          loading={ filesLoading }
          selected={ attachedFilesValue }
          onChange={ onChangeFiles }
          description={ `Common files you always want sent with this email, such as your terms of service. These are attached in addition to any invoice or statement PDFs included when the email is sent.` }
          display={ attachedFilesDisplay }
          clearable
        />
        <input
          type={ `file` }
          ref={ fileInputRef }
          onChange={ onUploadFile }
          className={ `hidden` }
        />
        <div className={ `mt-6 flex justify-between` }>
          <Button
            color={ `white` }
            icon={ <ArrowUpTrayIcon className={ `ml-2 h-4 w-4` } /> }
            onClick={ () => fileInputRef.current?.click() }
            loading={ uploadingFile }
            disabled={ uploadingFile }
          >
            { `Upload new file` }
          </Button>
          <Button onClick={ () => setFilesModalOpen(false) }>
            { `Done` }
          </Button>
        </div>
      </Modal>

      <Modal
        title={ `Rewrite tone with AI` }
        open={ rewriteToneModalOpen }
        setOpen={ setRewriteToneModalOpen }
      >
        { /* Not a <form>: submit events bubble through the portal to the template form and would save it */ }
        <InputTextAddon
          label={ `New tone` }
          name={ `tone` }
          value={ tone }
          onChange={ ({ value }) => setTone(String(value)) }
          onKeyDown={ e => {
            if (e.key === `Enter`) {
              e.preventDefault();
              onRewriteTone();
            }
          } }
          placeholder={ `E.g. friendly but firm` }
          maxLength={ 40 }
          description={ `Describe the tone in up to ${MAX_TONE_WORDS} words. The subject and all content sections are rewritten in the editor, nothing is saved until you save the template.` }
        />
        <When condition={ toneWordCount > MAX_TONE_WORDS }>
          <Paragraph
            size={ `xs` }
            className={ `mt-1 text-red-600` }
          >
            { `Use ${MAX_TONE_WORDS} words or fewer` }
          </Paragraph>
        </When>
        <div className={ `mt-4 flex justify-end` }>
          <Button
            onClick={ onRewriteTone }
            loading={ rewriteToneLoading }
            loadingText={ `Rewriting...` }
            disabled={ toneWordCount < 1 || toneWordCount > MAX_TONE_WORDS || rewriteToneLoading }
          >
            { `Rewrite` }
          </Button>
        </div>
      </Modal>
    </Grid>
  );
}
